import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

export interface MediaItem {
  id: number;
  key: string;
  name: string;
  alt: string;
  folder: string;
  tags: string[];
  contentType: string;
  size: number;
  uploaded: string;
  url: string;
}

interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  folder?: string;
  supplierTag?: string;
}

const MediaPicker: React.FC<MediaPickerProps> = ({ isOpen, onClose, onSelect, folder = 'story', supplierTag }) => {
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    fetchMedia();
  }, [isOpen, folder, supplierTag]);

  const fetchMedia = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('folder', folder);
      if (supplierTag) {
        params.set('supplier_tag', supplierTag);
      }
      const res = await apiFetch(`/api/public/admin/media?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setMediaList(Array.isArray(data) ? data : []);
      } else if (res.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('獲取媒體列表失敗');
      }
    } catch {
      setError('獲取媒體列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('請選擇有效的圖片檔案（JPG、PNG、GIF、WEBP、SVG）');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (!supplierTag) {
        formData.append('folder', folder);
      }

      const res = await apiFetch('/api/public/admin/media/upload', {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        await fetchMedia();
      } else if (res.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '上傳失敗');
      }
    } catch {
      setError('上傳失敗');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelect = (url: string) => {
    onSelect(url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">選擇圖片</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">資料夾：{folder}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Upload area */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-brand-300 dark:border-gray-600 rounded-lg hover:border-brand-500 dark:hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />
            {uploading ? <Loader2 size={20} className="animate-spin text-brand-600" /> : <Upload size={20} className="text-brand-600" />}
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {uploading ? '正在上傳...' : '上傳新圖片到資料夾'}
            </span>
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 size={32} className="animate-spin mb-3" />
              <p>載入中...</p>
            </div>
          ) : mediaList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Image size={48} className="mb-3 text-gray-300 dark:text-gray-600" />
              <p>此資料夾尚未上傳任何圖片</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {mediaList.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item.url)}
                  className="group text-left rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-brand-500 dark:hover:border-brand-500 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <div className="aspect-square bg-gray-100 dark:bg-gray-900 overflow-hidden">
                    <img
                      src={item.url}
                      alt={item.alt || item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800">
                    <p className="text-xs font-medium text-gray-800 dark:text-white truncate" title={item.name}>
                      {item.name}
                    </p>
                    {item.alt && item.alt !== item.name && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate" title={item.alt}>
                        {item.alt}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaPicker;
